/**
 * Плоский список строк таблицы: бренды и раскрытые под ними категории (§4.3).
 *
 * Виртуализатор работает именно по этому списку, поэтому индексы строк здесь —
 * те же, что в курсоре ячейки (§5.1) и в снимке строк (§5.4).
 */
import { useEffect, useState } from 'react'
import type { Brand, BrandCategory, ChannelCode } from '@shared/types.ts'
import { subscribePrefs } from '../../../app/prefs.js'
import type { ColumnDef } from './columns.js'
import type { Overlay } from '../../../lib/draft-overlay.js'

export type TableRow =
  | { kind: 'brand'; key: string; brand: Brand }
  | { kind: 'category'; key: string; brand: Brand; category: BrandCategory; last: boolean; detached: boolean }
  | { kind: 'no-categories'; key: string; brand: Brand }
  /* Хвост списка категорий: кнопка «+ Добавить категорию» под последней. */
  | { kind: 'add-category'; key: string; brand: Brand }

/** Порядок тиров для сортировки категорий внутри бренда (§4.3). */
const TIER_RANK: Record<string, number> = { 'A+': 0, A: 1, B: 2, C: 3 }

function tierRank(value: string | null | undefined): number {
  if (!value) return 9
  return TIER_RANK[value] ?? 8
}

/**
 * Строки в порядке показа. Категории сортируются тиром, затем алфавитом и в
 * сортировке верхнего уровня не участвуют (§4.3).
 */
export function buildRows(
  brands: readonly Brand[],
  expanded: ReadonlySet<number>,
  channel: ChannelCode,
  overlay: Overlay,
  collator: Intl.Collator,
): TableRow[] {
  const rows: TableRow[] = []
  for (const brand of brands) {
    rows.push({ kind: 'brand', key: `b${brand.id}`, brand })
    if (!expanded.has(brand.id)) continue

    if (brand.categories.length === 0) {
      rows.push({ kind: 'no-categories', key: `b${brand.id}-none`, brand })
      continue
    }

    const sorted = brand.categories.slice().sort((a, b) => {
      const byTier = tierRank(a.channels[channel]?.tier) - tierRank(b.channels[channel]?.tier)
      if (byTier !== 0) return byTier
      return collator.compare(a.name, b.name)
    })

    sorted.forEach((category, index) => {
      rows.push({
        kind: 'category',
        key: `b${brand.id}-c${category.id}`,
        brand,
        category,
        last: index === sorted.length - 1,
        detached: overlay.detached.has(category.id),
      })
    })
    rows.push({ kind: 'add-category', key: `b${brand.id}-add`, brand })
  }
  return rows
}

/** Применимость ячейки берётся из определения поля, а не из группы колонок (§4.3). */
export function isApplicable(column: ColumnDef, row: TableRow): boolean {
  if (column.role === 'gutter' || column.role === 'brand') return true
  if (row.kind === 'brand') return column.inBrandRow
  if (row.kind === 'category') return column.inCategoryRow
  return false
}

/** Бренд не ведётся в активном канале: диагональная штриховка, а не пустота (П3). */
export function isInChannel(brand: Brand, channel: ChannelCode): boolean {
  return brand.channels[channel] !== undefined
}

export interface RowHeights {
  brand: number
  category: number
  groupHead: number
  colHead: number
}

const FALLBACK: RowHeights = { brand: 28, category: 26, groupHead: 22, colHead: 26 }

function readPx(name: string, fallback: number): number {
  if (typeof window === 'undefined') return fallback
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  const parsed = Number.parseFloat(raw)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
}

function readHeights(): RowHeights {
  return {
    brand: readPx('--row-brand', FALLBACK.brand),
    category: readPx('--row-cat', FALLBACK.category),
    groupHead: readPx('--head-group', FALLBACK.groupHead),
    colHead: readPx('--head-col', FALLBACK.colHead),
  }
}

/**
 * Высоты строк — из токенов, а не из чисел в компоненте: плотность
 * «Просторная» меняет `--row-brand` и `--row-cat` на корне документа.
 */
export function useRowHeights(): RowHeights {
  const [heights, setHeights] = useState<RowHeights>(readHeights)
  useEffect(() => {
    setHeights(readHeights())
    return subscribePrefs(() => setHeights(readHeights()))
  }, [])
  return heights
}

export function rowHeight(row: TableRow, heights: RowHeights): number {
  return row.kind === 'brand' ? heights.brand : heights.category
}
